import { Navigate, useLocation } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { Layout } from "./Layout";
import { Button, LoadingBlock } from "./ui";

/** Gate in front of every console route. */
export function RequireAdmin() {
  const { status, user, logout } = useAuth();
  const location = useLocation();

  if (status === "loading") return <LoadingBlock label="Restoring session…" />;

  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  }

  // Signed in, but not staff. Say so plainly instead of looping them back to a
  // login form that would accept the same credentials all over again.
  if (status === "forbidden") {
    /*
     * Styled as its own full-height gate rather than a bare alert on a blank
     * page. It is the third thing in the auth flow after sign-in and register,
     * and it was the one screen still looking like a stack trace.
     */
    /*
     * `min-h-dvh` like every other full-height surface here: `screen` is the
     * viewport with iOS Safari's URL bar scrolled away, so a centred gate
     * measured against it sits low and leaves the page scrollable by the
     * height of the bar.
     */
    return (
      <div className="relative isolate grid min-h-dvh place-items-center overflow-hidden px-5 py-14">
        <div className="glow absolute inset-0 -z-20 opacity-60" aria-hidden="true" />
        <div className="grid-lines absolute inset-0 -z-10" aria-hidden="true" />

        <div className="w-full max-w-md">
          <p className="micro">Access denied</p>
          <h1 className="mt-5 text-title">This account is not staff</h1>

          <div className="panel mt-8 p-6">
            <p className="text-sm leading-6 text-fg-muted">
              <span className="ident">{user?.email}</span> is signed in, but is not a platform
              administrator, so Maxlayer Operations is not available to it.
            </p>
            <p className="mt-3 text-sm leading-6 text-fg-muted">
              If you were looking for your own apps, use the customer dashboard instead.
            </p>

            <Button variant="secondary" className="mt-6 w-full" onClick={() => void logout()}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 2FA is mandatory for staff. The API enforces this too (403 totp_required);
  // this gate sends them to enroll instead of letting them hit wall after wall.
  // `/security` itself must stay reachable, hence the path check.
  if (status === "authenticated" && user && !user.totpEnabled && location.pathname !== "/security") {
    return <Navigate to="/security" replace />;
  }

  return <Layout />;
}
